import { App, Button, Form, Input, Skeleton, Tooltip, Typography } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate, useParams } from "react-router-dom"
import { useTheme } from "styled-components"
import { ContainerFull, LabeledBox } from "../../components"
import { ShowMessagePage } from "../../config"
import { getUserGroup, updateUserGroup } from "../../endpoints"

const { TextArea } = Input

export function UserGroupDetails(): JSX.Element {
    const navigate = useNavigate()
    const theme = useTheme()
    const { groupId } = useParams()
    const queryClient = useQueryClient()
    const { modal } = App.useApp()
    const [form] = Form.useForm()

    const { isPending, isError, data, error } = useQuery({
        queryKey: ["getUserGroup", groupId],
        queryFn: () => getUserGroup({ groupId: parseInt(groupId ?? "0") }),
        refetchOnMount: "always",
    })

    const updateMutation = useMutation({
        mutationFn: updateUserGroup,
        onSuccess: (data) => {
            if (data.code === 1000) {
                modal.success({ content: "User group updated successfully" })
                queryClient.invalidateQueries({ queryKey: ["getUserGroup", groupId] })
                queryClient.invalidateQueries({ queryKey: ["getUserGroups"] })
            } else {
                modal.error({ content: data.message })
            }
        },
        onError: (error) => {
            modal.error({ content: "Failed to update user group" })
        },
    })

    const handleSave = () => {
        form.validateFields().then((values) => {
            // Trim all string values before sending to backend
            const trimmedValues = Object.keys(values).reduce((acc, key) => {
                const value = values[key]
                acc[key] = typeof value === 'string' ? value.trim() : value
                return acc
            }, {} as any)

            updateMutation.mutate({
                id: parseInt(groupId ?? "0"),
                ...trimmedValues,
            })
        })
    }

    if (isPending) {
        return <Skeleton />
    }

    if (isError) {
        return ShowMessagePage("Error", error?.message ?? "unknown error")
    }

    if (data.code !== 1000 || !data.group) {
        return ShowMessagePage("Error", data.message || "User group not found")
    }

    const group = data.group

    // Set form initial values
    if (group) {
        form.setFieldsValue({
            name: group.name,
            description: group.description,
            api_key: group.api_key,
            aws_profile: group.aws_profile,
            aws_bucket_name: group.aws_bucket_name,
            fileprefix: group.fileprefix,
            billingInstitution: group.billingInstitution,
            billingContactName: group.billingContactName,
            billingEmail: group.billingEmail,
            billingAddressLine1: group.billingAddressLine1,
            billingAddressLine2: group.billingAddressLine2,
            billingCity: group.billingCity,
            billingState: group.billingState,
            billingCountry: group.billingCountry || 'USA',
            billingZipcode: group.billingZipcode,
            copy_emails: group.copy_emails,
        })
    }

    return (
        <ContainerFull style={{ fontFamily: '"Rubik", sans-serif' }}>
            <Button
                type="link"
                key="backbutton"
                style={{ color: theme.primary }}
                onClick={() => {
                    navigate("/admin/usergroups")
                }}
            >
                {"<< Back to User Groups"}
            </Button>
            <LabeledBox title={`Edit User Group: ${group.name}`}>
                <Form
                    form={form}
                    layout="vertical"
                    name="userGroupEditForm"
                >
                    <Form.Item
                        label="Group Name"
                        name="name"
                        rules={[
                            { required: true, message: "Please enter a group name" },
                            { max: 100, message: "Group name cannot exceed 100 characters" },
                        ]}
                    >
                        <Input placeholder="Enter group name" />
                    </Form.Item>

                    {/* Display users with permissions */}
                    {group.users_receiving_notifications && group.users_receiving_notifications.length > 0 && (
                        <div style={{ 
                            marginBottom: 16, 
                            padding: 12, 
                            backgroundColor: '#f5f5f5', 
                            borderRadius: 4 
                        }}>
                            <Typography.Text strong>
                                Users Receiving Group Order Notifications: 
                                <Tooltip title="To change this, go to the user details page and turn it off">
                                    <InfoCircleOutlined style={{ marginLeft: 6, color: '#1890ff' }} />
                                </Tooltip>
                            </Typography.Text>
                            <div style={{ marginTop: 8 }}>
                                {group.users_receiving_notifications.map((user, index) => (
                                    <span key={user.id}>
                                        {user.firstName} {user.lastName}
                                        {index < group.users_receiving_notifications!.length - 1 ? ', ' : ''}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {group.users_viewing_orders && group.users_viewing_orders.length > 0 && (
                        <div style={{ 
                            marginBottom: 16, 
                            padding: 12, 
                            backgroundColor: '#f5f5f5', 
                            borderRadius: 4 
                        }}>
                            <Typography.Text strong>
                                Users Who Can View All Group Orders: 
                                <Tooltip title="To change this, go to the user details page and turn it off">
                                    <InfoCircleOutlined style={{ marginLeft: 6, color: '#1890ff' }} />
                                </Tooltip>
                            </Typography.Text>
                            <div style={{ marginTop: 8 }}>
                                {group.users_viewing_orders.map((user, index) => (
                                    <span key={user.id}>
                                        {user.firstName} {user.lastName}
                                        {index < group.users_viewing_orders!.length - 1 ? ', ' : ''}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    <Form.Item
                        label="Description"
                        name="description"
                        rules={[
                            { max: 500, message: "Description cannot exceed 500 characters" },
                        ]}
                    >
                        <TextArea 
                            rows={3} 
                            placeholder="Enter group description (optional)" 
                        />
                    </Form.Item>

                    <Form.Item
                        label="CC Order Emails To"
                        name="copy_emails"
                        extra="Email addresses to CC on order notification emails, separated by commas"
                        rules={[
                            { max: 500, message: "CC emails cannot exceed 500 characters" },
                        ]}
                    >
                        <Input placeholder="e.g. alice@example.com, bob@example.com" />
                    </Form.Item>

                    <Form.Item
                        label="API Key"
                        name="api_key"
                        rules={[
                            { max: 200, message: "API key cannot exceed 200 characters" },
                        ]}
                    >
                        <Input.Password placeholder="Enter API key (optional)" />
                    </Form.Item>

                    <Form.Item
                        label="AWS Profile"
                        name="aws_profile"
                        rules={[
                            { max: 100, message: "AWS profile cannot exceed 100 characters" },
                        ]}
                    >
                        <Input placeholder="Enter AWS profile (optional)" />
                    </Form.Item>

                    <Form.Item
                        label="AWS Bucket Name"
                        name="aws_bucket_name"
                        rules={[
                            { max: 300, message: "AWS bucket name cannot exceed 300 characters" },
                        ]}
                    >
                        <Input placeholder="Enter AWS S3 bucket name" />
                    </Form.Item>

                    <Form.Item
                        label="File Prefix"
                        name="fileprefix"
                        rules={[
                            { max: 100, message: "File prefix cannot exceed 100 characters" },
                        ]}
                    >
                        <Input placeholder="Enter file prefix (optional)" />
                    </Form.Item>

                    <div style={{ marginTop: 24, marginBottom: 16 }}>
                        <Typography.Title level={5}>Billing Information</Typography.Title>
                    </div>

                    <Form.Item
                        label="Billing Institution"
                        name="billingInstitution"
                        rules={[
                            { max: 200, message: "Institution name cannot exceed 200 characters" },
                        ]}
                    >
                        <Input placeholder="Enter institution/company name" />
                    </Form.Item>

                    <Form.Item
                        label="Billing Contact Name"
                        name="billingContactName"
                        rules={[
                            { max: 100, message: "Contact name cannot exceed 100 characters" },
                        ]}
                    >
                        <Input placeholder="Enter billing contact name" />
                    </Form.Item>

                    <Form.Item
                        label="Billing Email"
                        name="billingEmail"
                        rules={[
                            { 
                                validator: (_, value) => {
                                    if (!value) return Promise.resolve()
                                    const trimmedValue = value.trim()
                                    if (!trimmedValue) return Promise.resolve()
                                    
                                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                                    if (emailRegex.test(trimmedValue)) {
                                        return Promise.resolve()
                                    }
                                    return Promise.reject(new Error('Please enter a valid email address'))
                                }
                            },
                            { max: 100, message: "Email cannot exceed 100 characters" },
                        ]}
                    >
                        <Input placeholder="Enter billing email address" />
                    </Form.Item>

                    <Form.Item
                        label="Address Line 1"
                        name="billingAddressLine1"
                        rules={[
                            { max: 200, message: "Address line 1 cannot exceed 200 characters" },
                        ]}
                    >
                        <Input placeholder="Enter street address" />
                    </Form.Item>

                    <Form.Item
                        label="Address Line 2"
                        name="billingAddressLine2"
                        rules={[
                            { max: 200, message: "Address line 2 cannot exceed 200 characters" },
                        ]}
                    >
                        <Input placeholder="Enter apartment, suite, etc. (optional)" />
                    </Form.Item>

                    <div style={{ display: 'flex', gap: 8 }}>
                        <Form.Item
                            label="City"
                            name="billingCity"
                            style={{ flex: 1 }}
                            rules={[
                                { max: 100, message: "City cannot exceed 100 characters" },
                            ]}
                        >
                            <Input placeholder="Enter city" />
                        </Form.Item>

                        <Form.Item
                            label="State"
                            name="billingState"
                            style={{ width: 160 }}
                            rules={[
                                { max: 30, message: "State cannot exceed 30 characters" },
                            ]}
                        >
                            <Input placeholder="State" />
                        </Form.Item>

                        <Form.Item
                            label="ZIP Code"
                            name="billingZipcode"
                            style={{ width: 120 }}
                            rules={[
                                { max: 20, message: "ZIP code cannot exceed 20 characters" },
                            ]}
                        >
                            <Input placeholder="ZIP" />
                        </Form.Item>
                    </div>

                    <Form.Item
                        label="Country"
                        name="billingCountry"
                        rules={[
                            { max: 50, message: "Country cannot exceed 50 characters" },
                        ]}
                    >
                        <Input placeholder="Enter country" />
                    </Form.Item>

                    <Form.Item>
                        <Button 
                            type="primary" 
                            onClick={handleSave}
                            loading={updateMutation.isPending}
                            style={{ marginRight: 8 }}
                        >
                            Save Changes
                        </Button>
                        <Button onClick={() => navigate("/admin/usergroups")}>
                            Cancel
                        </Button>
                    </Form.Item>
                </Form>
            </LabeledBox>
        </ContainerFull>
    )
}
